import Awake20 from '@carbon/icons-react/es/awake/20';
import Calendar20 from '@carbon/icons-react/es/calendar/20';
import EventSchedule20 from '@carbon/icons-react/es/event--schedule/20';
import UserAdmin20 from '@carbon/icons-react/es/user--admin/20';
import { groupBy, sortBy } from 'lodash';
import moment from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Company, Team, User } from 'api/models';
import { UserProfileInfo } from 'api/user';
import { ReactComponent as NextWeek20 } from 'assets/svgs/next-week.svg';
import { ReactComponent as ThisWeek20 } from 'assets/svgs/this-week.svg';
import { Avatar } from 'components/atoms/avatar';
import { CalendarIcon } from 'components/atoms/calendar-icon';
import { TaskCompleted } from 'components/atoms/icons';
import { Task } from 'data/tasks';
import { unreachable } from 'utils/unreachable';
import { AssignedToValue, GroupByValue, ViewValue } from '../filter';

interface RenderData<T> {
  items: Task[];
  key: T;
  groupBy: GroupByValue;
  label: string | React.ReactNode;
  icon?: React.ReactNode;
}

export enum DueDateOrder {
  Overdue = 'OVERDUE',
  Today = 'TODAY',
  Tomorrow = 'TOMORROW',
  ThisWeek = 'THIS_WEEK',
  NextWeek = 'NEXT_WEEK',
  Later = 'LATER',
  NoDue = 'NO_DUE',
  Completed = 'COMPLETED',
}

export const DUE_DATE_ORDER = [
  DueDateOrder.Overdue,
  DueDateOrder.Today,
  DueDateOrder.Tomorrow,
  DueDateOrder.ThisWeek,
  DueDateOrder.NextWeek,
  DueDateOrder.Later,
  DueDateOrder.NoDue,
  DueDateOrder.Completed,
];

export const DueDateOrderIconHash: { [x in DueDateOrder]: JSX.Element } = {
  [DueDateOrder.Overdue]: <EventSchedule20 />,
  [DueDateOrder.Today]: <CalendarIcon date={moment().format('DD')} />,
  [DueDateOrder.Tomorrow]: <Awake20 />,
  [DueDateOrder.ThisWeek]: <ThisWeek20 />,
  [DueDateOrder.NextWeek]: <NextWeek20 />,
  [DueDateOrder.Later]: <EventSchedule20 />,
  [DueDateOrder.NoDue]: <Calendar20 />,
  [DueDateOrder.Completed]: <TaskCompleted />,
};

export enum Default {
  Csat = 'CSAT',
  Approvals = 'APPROVALS',
  RecentlyAssigned = 'RECENTLY_ASSIGNED',
}

export const DEFAULT_ORDER = [
  ...DUE_DATE_ORDER.slice(0, 1),
  Default.RecentlyAssigned,
  ...DUE_DATE_ORDER.slice(1),
];

const determineGroup = (task: Task) => {
  const { completedAt, dueDate } = task;

  if (completedAt != null) {
    return DueDateOrder.Completed;
  }

  if (dueDate == null) {
    return DueDateOrder.NoDue;
  }

  const now = moment();
  const today = now.clone().startOf('day');
  const tomorrow = now.clone().add(1, 'day').startOf('day');
  const nextWeek = now.clone().add(1, 'week');
  const value = moment(dueDate).startOf('day');

  /* NOTE:: Keep the order of the conditions as it is. */
  if (value.isBefore(today) && !completedAt) {
    return DueDateOrder.Overdue;
  }

  if (value.isSame(today)) {
    return DueDateOrder.Today;
  }

  if (value.isSame(tomorrow)) {
    return DueDateOrder.Tomorrow;
  }

  if (value.isSame(now, 'week')) {
    return DueDateOrder.ThisWeek;
  }

  if (value.isSame(nextWeek, 'week')) {
    return DueDateOrder.NextWeek;
  }

  if (value.isAfter(nextWeek, 'week')) {
    return DueDateOrder.Later;
  }

  return DueDateOrder.Overdue;
};

type DueData = RenderData<DueDateOrder>;

const getDueData = (data: Task[]): DueData[] => {
  const grouped = groupBy(data, (task) => determineGroup(task));
  const result: DueData[] = [];

  return DUE_DATE_ORDER.reduce((arr, key) => {
    const label = `filter.due_date.${key.toLowerCase()}`;
    const tasks = grouped[key];

    if (tasks != null) {
      const obj = Object.create({
        items: sortBy(grouped[key], 'dueDate'),
        key,
        label: <FormattedMessage id={label} />,
        icon: DueDateOrderIconHash[key],
      });

      return [...arr, obj];
    }

    return arr;
  }, result);
};

type ApprovalsTaskData = RenderData<Default.Approvals>;

const getRecentlyAssigned = (
  data: Task[],
  userProfileInfo: UserProfileInfo,
) => {
  if (!data.length) {
    return [];
  }
  const result = data.filter((t) => {
    const { assigneeAuditMeta = [] } = t;

    return (
      assigneeAuditMeta.filter((meta) => {
        const { assignee, assigneeType } = meta;
        const { user, teams } = userProfileInfo;
        let isMatching = false;

        if (assigneeType === 'USER') {
          const assignedUser = assignee as User;

          isMatching = assignedUser.userId === user.userId;
        }
        if (assigneeType === 'TEAM') {
          const team = assignee as Team;

          isMatching = (teams ?? [])
            .map((_team) => _team.teamId)
            .includes(team.teamId);
        }
        if (isMatching) {
          return moment(meta.assignedAt).isSameOrAfter(
            moment().subtract(2, 'days').startOf('day'),
          );
        }

        return isMatching;
      }).length > 0
    );
  });

  return [
    Object.create({
      items: sortBy(result, 'dueDate'),
      key: Default.RecentlyAssigned,
      label: <FormattedMessage id="recently_assigned" />,
      icon: <UserAdmin20 />,
    }),
  ];
};

const getDefaultGroupData = (
  data: Task[],
  filter: Filter,
  userProfileInfo: UserProfileInfo,
) => {
  const dueData = getDueData(data);
  const recentlyAssigned = getRecentlyAssigned(data, userProfileInfo);
  const result = [...dueData, ...recentlyAssigned];

  return sortBy(result, (item: DueData | ApprovalsTaskData) => {
    return DEFAULT_ORDER.indexOf(item.key);
  });
};

type ProjectData = RenderData<string>;
const getProjectGroupData = (
  data: Task[],
  userProfileInfo: UserProfileInfo,
): ProjectData[] => {
  const grouped = groupBy(data, 'project');
  const projects: {
    [key: number]: { customer: Company; projectName: string };
  } = userProfileInfo.projects.reduce((obj, nextItem) => {
    const current = {
      [nextItem.projectId]: {
        customer: nextItem.customer,
        projectName: nextItem.projectName,
      },
    };

    return {
      ...obj,
      ...current,
    };
  }, {});
  const returnArr: ProjectData[] = [];
  const result = Object.keys(grouped).reduce((arr, key) => {
    const obj = Object.create({
      items: grouped[key],
      key,
      label: projects[key]?.projectName,
      icon: (
        <Avatar
          shape="square"
          name={projects[key]?.projectName}
          src={projects[Number(key)]?.customer.companyLogoUrl}
        >
          {projects[key]?.companyName}
        </Avatar>
      ),
    });

    return [...arr, obj];
  }, returnArr);

  return sortBy(result, 'label');
};

export interface Filter {
  view: ViewValue;
  assignedTo: AssignedToValue;
  groupBy: GroupByValue;
}

export enum AllowedFilterItem {
  AssignedTo = 'assignedTo',
  GroupBy = 'groupBy',
}

export const getDataByView = (data: Task[], value: ViewValue) => {
  const now = moment();
  const today = now.clone().startOf('day');
  const yesterday = now.clone().subtract(1, 'days').startOf('day');
  const A_WEEK_OLD = now.clone().subtract(1, 'week').startOf('day');
  const TWO_WEEK_OLD = now.clone().subtract(2, 'weeks').startOf('day');
  const sinceToday = (t: Task) =>
    t.completedAt && moment(t.completedAt).startOf('day').isSame(today);
  const sinceYesterday = (t: Task) =>
    t.completedAt && moment(t.completedAt).isAfter(yesterday);
  const sinceLastWeek = (t: Task) =>
    t.completedAt && moment(t.completedAt).isAfter(A_WEEK_OLD);
  const sinceLastTwoWeek = (t: Task) =>
    t.completedAt && moment(t.completedAt).isAfter(TWO_WEEK_OLD);

  /* NOTE:: We shouldn't show overdue task when user choose completed view */
  /* const tasksThatAreNotOverDue = data.filter(
    (t) => t.dueDate && moment(t.dueDate).isSameOrAfter(TODAY),
  ); */

  switch (value) {
    case ViewValue.All:
      return data;
    case ViewValue.Incomplete:
      return data.filter((d) => !d.completedAt);
    case ViewValue.Today:
      return data.filter(sinceToday);
    case ViewValue.Yesterday:
      return data.filter(sinceYesterday);
    case ViewValue.LastOneWeek:
      return data.filter(sinceLastWeek);
    case ViewValue.LastTwoWeeks:
      return data.filter(sinceLastTwoWeek);
    default:
      return data;
  }
};

const getDataByAssignedTo = (
  data: Task[],
  value: AssignedToValue,
  userProfileInfo: UserProfileInfo,
) => {
  const currentUserId = userProfileInfo.user.userId;

  switch (value) {
    case AssignedToValue.Me:
      return data.filter((task) =>
        task.assignee?.users.find((u) => u.userId === currentUserId),
      );
    case AssignedToValue.Teams:
      // return tasks  which are not directly assigned me but are assigned to the teams i am part of.
      return data.filter((task) => {
        return !task.assignee?.users.find((u) => u.userId === currentUserId);
      });
    case AssignedToValue.Both:
      return data;
    default:
      return unreachable(value);
  }
};

export const getDataByGroupBy = (
  data: Task[],
  filter: Filter,
  userProfileInfo: UserProfileInfo,
): ProjectData[] => {
  switch (filter.groupBy) {
    case GroupByValue.Default:
      return getDefaultGroupData(data, filter, userProfileInfo);
    case GroupByValue.DueDate:
      return getDueData(data);
    case GroupByValue.Project:
      return getProjectGroupData(
        data,
        userProfileInfo,
      ) as unknown as ProjectData[];
    default:
      throw new Error(`cannot be reached ${groupBy}`);
  }
};

export const getDataByGroupByFilter = (
  data: Task[],
  filter: Omit<Filter, 'assignedTo'>,
  userProfileInfo: UserProfileInfo,
) => {
  switch (filter.groupBy) {
    case GroupByValue.DueDate:
      return getDueData(data);
    case GroupByValue.Project:
      return getProjectGroupData(
        data,
        userProfileInfo,
      ) as unknown as ProjectData[];
    default:
      throw new Error(`cannot be reached ${groupBy}`);
  }
};

export const getFilteredData = (
  data: Task[],
  filter: Filter,
  userProfileInfo: UserProfileInfo,
) => {
  return getDataByGroupBy(
    getDataByAssignedTo(
      getDataByView(data, filter.view),
      filter.assignedTo,
      userProfileInfo,
    ),
    filter,
    userProfileInfo,
  );
};
